// Implements: F-MCK-021, F-IDN-025
/**
 * The share popover's "Personas con acceso" panel (mockup v3 :1651-1656).
 *
 * The owner is `MockupDetailView.owner_id` (never among the assignments unless assigned), drawn
 * first with "Propietario"; then the explicit assignments, named by joining their `user_id` with the
 * organization's people (an id not among them reads "Persona sin acceso a la organización"). Only
 * the owner edits (`your_role`): each write is sent once and the list is redrawn from the server's
 * answer, never optimistically. A personal mockup shows only its owner and "Solo tú tienes acceso."
 * (default P4-5) and reads no assignments. Every query is keyed by the mockup id and the page mounts
 * the panel keyed by it, so an answer for another mockup never lands here (checklist 14).
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";

import { asApiError } from "../../../shared/api/asApiError";
import { fetchMockupDetail } from "../../../shared/api/mockupDetail";
import { fetchOrganizationPeople } from "../../../shared/api/people";
import { useViewerId } from "../../../shared/api/viewer";
import { mockupAssignmentsKey, mockupDetailKey, organizationPeopleKey } from "../../../shared/api/queryKeys";
import { useTranslation } from "../../../shared/i18n";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { assignRole, fetchAssignments, revokeAssignment } from "../api/accessApi";
import { AccessList } from "../components/AccessList";
import type { AccessPerson, AccessRow, AssignableRole } from "../components/AccessList";

export interface MockupAccessContainerProps {
  readonly mockupId: string;
  /** Opens the transfer of ownership (through the editor's "Más acciones"). */
  readonly onTransfer?: (() => void) | undefined;
}

export function MockupAccessContainer({ mockupId, onTransfer }: MockupAccessContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const sending = useRef(false);
  const viewerId = useViewerId();

  const detail = useQuery({ queryKey: mockupDetailKey(mockupId), queryFn: () => fetchMockupDetail(mockupId) });
  const organizationId = detail.data?.organization_id ?? null;
  const personal = detail.data !== undefined && (detail.data.scope_kind === "personal" || organizationId === null);
  const assignments = useQuery({
    queryKey: mockupAssignmentsKey(mockupId),
    queryFn: ({ signal }) => fetchAssignments(mockupId, signal),
    enabled: detail.data !== undefined && !personal,
  });
  const people = useQuery({
    queryKey: organizationPeopleKey(organizationId ?? ""),
    queryFn: ({ signal }) => fetchOrganizationPeople(organizationId ?? "", signal),
    enabled: organizationId !== null && !personal,
  });

  const view = useMemo(() => {
    if (detail.data === undefined) return null;
    const known = new Map((people.data ?? []).map((member) => [member.user.id, member.user]));
    const ownerId = detail.data.owner_id;
    const named = (userId: string): { name: string; email: string } => {
      const user = known.get(userId);
      return user === undefined
        ? { name: t("sharing.people.unknown"), email: "" }
        : { name: user.full_name, email: user.email };
    };
    const owner: AccessRow = personal
      ? {
          userId: ownerId,
          name: viewerId === ownerId ? t("sharing.people.you") : t("sharing.people.owner"),
          email: "",
          role: "owner",
        }
      : { userId: ownerId, ...named(ownerId), role: "owner" };
    const rows: AccessRow[] = [
      owner,
      ...(personal ? [] : (assignments.data ?? []))
        .filter((assignment) => assignment.user_id !== ownerId)
        .map((assignment) => ({ userId: assignment.user_id, ...named(assignment.user_id), role: assignment.role })),
    ];
    const taken = new Set(rows.map((row) => row.userId));
    const candidates: AccessPerson[] = (people.data ?? [])
      // An absent status is a team lead's view (P3-2): the server already kept the active seats.
      .filter((member) => !taken.has(member.user.id) && (member.status ?? "active") === "active")
      .map((member) => ({ id: member.user.id, name: member.user.full_name }));
    return { rows, candidates };
  }, [assignments.data, detail.data, people.data, personal, t, viewerId]);

  if (detail.isError || assignments.isError || people.isError)
    return <p role="alert">{t("sharing.people.failed")}</p>;
  if (view === null || (!personal && (assignments.isPending || people.isPending)))
    return <p>{t("sharing.people.loading")}</p>;

  const write = async (send: () => Promise<unknown>): Promise<void> => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    setError(undefined);
    try {
      await send();
      await queryClient.invalidateQueries({ queryKey: mockupAssignmentsKey(mockupId) });
    } catch (cause) {
      setError(userMessage(asApiError(cause), t("sharing.people.changeFailed")));
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };

  return (
    <AccessList
      rows={view.rows}
      candidates={view.candidates}
      editable={detail.data?.your_role === "owner" && !personal}
      personal={personal}
      viewerOwns={viewerId !== null && viewerId === detail.data?.owner_id}
      busy={busy}
      error={error}
      onRole={(userId, role: AssignableRole) => {
        void write(() => assignRole(mockupId, userId, role));
      }}
      onRemove={(userId) => {
        void write(() => revokeAssignment(mockupId, userId));
      }}
      onAdd={(userId, role) => {
        void write(() => assignRole(mockupId, userId, role));
      }}
      onTransfer={onTransfer}
    />
  );
}
